import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import api, { ensureCsrfCookie } from "../axiosConfig";
import { campaignApi } from "../services/campaignApi";
import AudienceBuilder from "./whatsappBroadcast/AudienceBuilder";
import AudienceReview from "./whatsappBroadcast/AudienceReview";
import CampaignConfirmation from "./whatsappBroadcast/CampaignConfirmation";

const steps = ["Choose Audience", "Review Audience", "Write Message", "Confirm & Schedule"];

const defaultForm = {
  name: "",
  whatsapp_template_id: "",
  messages_per_hour: 200,
  cooldown_hours: 24,
  // Messages only go out inside this daily window (campaign timezone).
  send_window_start: "08:00",
  send_window_end: "17:00",
  timezone: "Africa/Harare",
};

const defaultFilters = {
  q: "",
  source: "",
  status: "",
  contact_group_id: "",
  limit: 200,
  preferred_limit: "",
  target_audience: "all",
};

// <input type="time"> yields HH:MM; the API expects HH:MM:SS.
function toApiTime(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return null;
  }

  return trimmed.length === 5 ? `${trimmed}:00` : trimmed;
}

function toLocalDatetimeValue(date) {
  const pad = (value) => String(value).padStart(2, "0");
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const min = pad(date.getMinutes());

  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

function safeStr(value) {
  return String(value ?? "").trim();
}

function schemaKeysSorted(schema) {
  const source = schema && typeof schema === "object" ? schema : {};
  return Object.keys(source)
    .map(String)
    .filter(Boolean)
    .sort((left, right) => Number(left) - Number(right));
}

function labelForVar(key, schema) {
  const label = schema?.[String(key)];
  if (label) {
    return String(label);
  }

  return `Variable {{${key}}}`;
}

function parseTemplatePlaceholders(text) {
  const matches = String(text || "").match(/\{\{\s*(\d+)\s*\}\}/g) || [];
  const keys = [];
  const seen = new Set();

  matches.forEach((token) => {
    const match = token.match(/\d+/);
    if (!match) {
      return;
    }

    const key = String(match[0]);
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    keys.push(key);
  });

  return keys;
}

function normalizedTemplateComponents(template) {
  return Array.isArray(template?.components_schema)
    ? template.components_schema.filter((component) => component && typeof component === "object")
    : [];
}

function isValidHttpUrl(value) {
  const raw = safeStr(value);
  if (!raw) {
    return false;
  }

  try {
    const parsed = new URL(raw);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function isLikelyMetaSampleMediaUrl(value, sampleUrl = "") {
  const raw = safeStr(value);
  if (!isValidHttpUrl(raw)) {
    return false;
  }

  const knownSampleUrl = safeStr(sampleUrl);
  if (knownSampleUrl && raw === knownSampleUrl) {
    return true;
  }

  try {
    const host = String(new URL(raw).hostname || "").toLowerCase();
    return host === "scontent.whatsapp.net" || host.endsWith(".scontent.whatsapp.net");
  } catch {
    return false;
  }
}

function headerMediaAcceptForFormat(format) {
  switch (safeStr(format).toUpperCase()) {
    case "IMAGE":
      return ".jpg,.jpeg,.png,image/jpeg,image/png";
    case "VIDEO":
      return ".mp4,.3gp,video/mp4,video/3gpp";
    case "DOCUMENT":
      return ".pdf,.txt,.doc,.docx,.ppt,.pptx,.xls,.xlsx,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    default:
      return "*";
  }
}

function renderTemplateText(text, vars, fallback = {}) {
  const merged = { ...fallback, ...vars };

  if (!safeStr(merged["1"])) {
    merged["1"] = safeStr(fallback["1"]) || "FirstName";
  }

  return String(text || "").replace(/\{\{\s*(\d+)\s*\}\}/g, (_, digits) => {
    const key = String(digits);
    return merged[key] ?? "";
  });
}

function templateComponentType(component) {
  return safeStr(component?.type).toUpperCase();
}

function extractTemplateExampleMediaUrl(component) {
  const handleList = component?.example?.header_handle;
  if (Array.isArray(handleList) && isValidHttpUrl(handleList[0])) {
    return safeStr(handleList[0]);
  }

  const directLink = safeStr(component?.example?.header_url || component?.example?.url);
  return isValidHttpUrl(directLink) ? directLink : "";
}

function buildTemplateExampleDefaults(template) {
  const defaults = {};

  normalizedTemplateComponents(template).forEach((component) => {
    const type = templateComponentType(component);

    if (type === "BUTTONS") {
      const buttons = Array.isArray(component?.buttons) ? component.buttons : [];

      buttons.forEach((button, index) => {
        const buttonType = safeStr(button?.type).toUpperCase();

        if (buttonType === "COPY_CODE") {
          const couponCode = Array.isArray(button?.example) ? safeStr(button.example[0]) : safeStr(button?.example);
          if (couponCode) {
            defaults[`button_${index}_coupon_code`] = couponCode;
          }
        }
      });
    }
  });

  return defaults;
}

function buildTemplateParameterFields(template) {
  const schema = template?.variables_schema || {};
  const fields = [];
  const seen = new Set();

  const pushField = (field) => {
    if (!field?.key || seen.has(field.key)) {
      return;
    }

    seen.add(field.key);
    fields.push({
      required: true,
      helperText: "Required.",
      ...field,
    });
  };

  schemaKeysSorted(schema)
    .filter((key) => key !== "1")
    .forEach((key) => {
      pushField({
        key,
        label: `${labelForVar(key, schema)} ({{${key}}})`,
      });
    });

  normalizedTemplateComponents(template).forEach((component) => {
    const type = safeStr(component?.type).toUpperCase();

    if (type === "HEADER") {
      const format = safeStr(component?.format).toUpperCase();

      if (format === "TEXT") {
        parseTemplatePlaceholders(component?.text).forEach((placeholder) => {
          pushField({
            key: `header_${placeholder}`,
            label: `Header variable {{${placeholder}}}`,
          });
        });
      }

      if (["IMAGE", "VIDEO", "DOCUMENT"].includes(format)) {
        pushField({
          key: "header_media_link",
          label: `Header ${format.toLowerCase()} URL`,
          helperText: `Enter a public http(s) ${format.toLowerCase()} URL that Meta can fetch.`,
          inputType: "url",
        });
        pushField({
          key: "header_media_id",
          label: `Header ${format.toLowerCase()} media ID`,
          required: false,
          hidden: true,
        });

        if (format === "DOCUMENT") {
          pushField({
            key: "header_document_filename",
            label: "Header document filename",
            helperText: "Optional file name shown to the recipient.",
            required: false,
          });
        }
      }
    }

    if (type === "BUTTONS") {
      const buttons = Array.isArray(component?.buttons) ? component.buttons : [];

      buttons.forEach((button, index) => {
        const buttonType = safeStr(button?.type).toUpperCase();
        const buttonText = safeStr(button?.text) || `Button ${index + 1}`;

        if (buttonType === "COPY_CODE") {
          pushField({
            key: `button_${index}_coupon_code`,
            label: `${buttonText} coupon code`,
            helperText: "Required for copy code buttons.",
          });
        }

        if (buttonType === "URL") {
          parseTemplatePlaceholders(button?.url).forEach((placeholder) => {
            pushField({
              key: `button_${index}_${placeholder}`,
              label: `${buttonText} URL value {{${placeholder}}}`,
              helperText: "Value injected into the dynamic button URL.",
            });
          });
        }
      });
    }
  });

  return fields;
}

function formatWhatsAppTime(date = new Date()) {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getDefaultsForTemplate(template) {
  const exampleDefaults = buildTemplateExampleDefaults(template);
  const name = safeStr(template?.name);
  const id = String(template?.id ?? "");

  if (name === "app_launch_cta" || id === "3") {
    return {
      ...exampleDefaults,
      "2": "com.abisiniya.travel",
      "3": "6744955496",
    };
  }

  if (name === "app_trip_discount_percent") {
    return {
      ...exampleDefaults,
      "2": "com.abisiniya.travel",
      "3": "6744955496",
      "4": "ABIS10",
      "5": "10",
    };
  }

  return exampleDefaults;
}

async function fetchWhatsAppTemplates() {
  await ensureCsrfCookie();
  const response = await api.get("/whatsapp/templates");
  const rows = response?.data?.data;

  if (!Array.isArray(rows)) {
    throw new Error("Templates API returned an unexpected response.");
  }

  return rows;
}

function formatTemplateSyncTime(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function humanizeTemplateMeta(value) {
  return safeStr(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function describeTemplateForMenu(template) {
  const parts = [];
  const category = humanizeTemplateMeta(template?.category);
  const language = safeStr(template?.language_code);
  const componentCount = normalizedTemplateComponents(template).length;

  if (category) {
    parts.push(category);
  }

  if (language) {
    parts.push(language);
  }

  if (componentCount > 0) {
    parts.push(`${componentCount} component${componentCount === 1 ? "" : "s"}`);
  }

  return parts.join(" | ");
}

function resolveTemplateHeaderPreview(template, vars, uploadedHeaderMedia = null) {
  const header = normalizedTemplateComponents(template).find((component) => templateComponentType(component) === "HEADER");
  if (!header) {
    return null;
  }

  const format = safeStr(header?.format).toUpperCase();

  if (format === "TEXT") {
    const text = renderTemplateText(header?.text, vars, { "1": "Addison" });
    return text ? { format, text } : null;
  }

  if (["IMAGE", "VIDEO", "DOCUMENT"].includes(format)) {
    const uploadedMediaId = safeStr(vars?.header_media_id);
    if (uploadedHeaderMedia && uploadedMediaId && uploadedHeaderMedia.mediaId === uploadedMediaId) {
      return {
        format,
        url: uploadedHeaderMedia.previewUrl || uploadedHeaderMedia.filename || uploadedMediaId,
        filename: uploadedHeaderMedia.filename,
        fromMetaSample: false,
        fromUploadedMedia: true,
      };
    }

    const enteredUrl = safeStr(vars?.header_media_link);
    const sampleUrl = extractTemplateExampleMediaUrl(header);
    const hasValidEnteredUrl = isValidHttpUrl(enteredUrl);
    const enteredUrlIsMetaSample = hasValidEnteredUrl && isLikelyMetaSampleMediaUrl(enteredUrl, sampleUrl);
    const mediaUrl = hasValidEnteredUrl ? enteredUrl : sampleUrl;

    return mediaUrl
      ? {
        format,
        url: mediaUrl,
        filename: safeStr(vars?.header_document_filename),
        fromMetaSample: enteredUrlIsMetaSample || (!hasValidEnteredUrl && Boolean(sampleUrl)),
      }
      : null;
  }

  return null;
}

function resolveTemplateFooterPreview(template, vars) {
  const footer = normalizedTemplateComponents(template).find((component) => templateComponentType(component) === "FOOTER");
  if (!footer) {
    return "";
  }

  return renderTemplateText(footer?.text, vars, { "1": "Addison" });
}

function resolveTemplateButtonsPreview(template, vars) {
  const component = normalizedTemplateComponents(template).find((item) => templateComponentType(item) === "BUTTONS");
  if (!component) {
    return [];
  }

  const buttons = Array.isArray(component?.buttons) ? component.buttons : [];

  return buttons.map((button, index) => {
    const type = safeStr(button?.type).toUpperCase();
    const text = safeStr(button?.text) || `Button ${index + 1}`;

    if (type === "COPY_CODE") {
      const exampleValue = Array.isArray(button?.example) ? safeStr(button.example[0]) : safeStr(button?.example);
      const couponCode = safeStr(vars?.[`button_${index}_coupon_code`]) || exampleValue;

      return {
        type,
        text,
        value: couponCode,
      };
    }

    if (type === "URL") {
      return {
        type,
        text,
        value: renderTemplateText(button?.url, vars, { "1": "Addison" }),
      };
    }

    if (type === "PHONE_NUMBER") {
      return {
        type,
        text,
        value: safeStr(button?.phone_number),
      };
    }

    return {
      type,
      text,
      value: "",
    };
  });
}

function fieldValidationMessage(field, value, template = null, vars = {}) {
  const raw = safeStr(value);
  const headerComponent = normalizedTemplateComponents(template).find((component) => templateComponentType(component) === "HEADER");
  const headerSampleUrl = extractTemplateExampleMediaUrl(headerComponent);
  const uploadedMediaId = safeStr(vars?.header_media_id);

  if (field.key === "header_media_link" && uploadedMediaId) {
    return "";
  }

  if (field.required !== false && !raw) {
    if (field.key === "header_media_link") {
      return "Required. Upload a file to Meta or enter your own public http(s) media URL for the header.";
    }

    return field.helperText || "Required.";
  }

  if (field.key === "header_media_link" && raw && !isValidHttpUrl(raw)) {
    return "Enter a valid public http(s) URL for the header media.";
  }

  if (field.key === "header_media_link" && raw && isLikelyMetaSampleMediaUrl(raw, headerSampleUrl)) {
    return "Meta sample media URLs are preview-only. Enter your own public image, video, or document URL.";
  }

  return "";
}

function WhatsAppPhonePreview({ body, vars, template, uploadedHeaderMedia = null }) {
  const fullPreviewText = renderTemplateText(body, vars, {
    "1": vars?.["1"]?.trim() ? vars["1"] : "Addison",
  });
  const timeLabel = useMemo(() => formatWhatsAppTime(), []);
  const headerPreview = useMemo(
    () => resolveTemplateHeaderPreview(template, vars, uploadedHeaderMedia),
    [template, uploadedHeaderMedia, vars]
  );
  const footerPreview = useMemo(() => resolveTemplateFooterPreview(template, vars), [template, vars]);
  const buttonsPreview = useMemo(() => resolveTemplateButtonsPreview(template, vars), [template, vars]);

  const renderHeader = () => {
    if (!headerPreview) {
      return null;
    }

    if (headerPreview.format === "IMAGE") {
      return (
        <Box sx={{ position: "relative", bgcolor: "#E5E7EB" }}>
          <Box
            component="img"
            src={headerPreview.url}
            alt="Template header"
            sx={{
              display: "block",
              width: "100%",
              height: { xs: 124, md: 148 },
              objectFit: "cover",
              bgcolor: "#E5E7EB",
            }}
          />
          {headerPreview.fromMetaSample || headerPreview.fromUploadedMedia ? (
            <Box
              sx={{
                position: "absolute",
                right: 8,
                bottom: 8,
                px: 0.8,
                py: 0.25,
                borderRadius: 999,
                bgcolor: "rgba(17,24,39,0.78)",
                color: "white",
                fontSize: 8,
                fontWeight: 700,
              }}
            >
              {headerPreview.fromUploadedMedia ? "Uploaded file" : "Meta sample"}
            </Box>
          ) : null}
        </Box>
      );
    }

    if (headerPreview.format === "DOCUMENT") {
      return (
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            bgcolor: "#F8FAFC",
          }}
        >
          <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#111827" }}>
            {headerPreview.filename || "Document attachment"}
          </Typography>
          <Typography sx={{ fontSize: 9, color: "#6B7280", mt: 0.35 }}>
            {headerPreview.url}
          </Typography>
        </Box>
      );
    }

    if (headerPreview.format === "VIDEO") {
      return (
        <Box
          sx={{
            px: 1.5,
            py: 4,
            bgcolor: "#111827",
            color: "white",
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 800 }}>Video header</Typography>
          <Typography sx={{ fontSize: 9, color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
            {headerPreview.url}
          </Typography>
        </Box>
      );
    }

    if (headerPreview.format === "TEXT") {
      return (
        <Typography
          sx={{
            px: 1.5,
            pt: 1.5,
            fontSize: { xs: 11.2, md: 12.2 },
            fontWeight: 800,
            lineHeight: 1.35,
            color: "#111827",
          }}
        >
          {headerPreview.text}
        </Typography>
      );
    }

    return null;
  };

  return (
    <Box sx={{ border: "1px solid #E9ECEF", borderRadius: 3, p: { xs: 1.5, md: 2 }, bgcolor: "white", height: "100%" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={900}>
          Message preview
        </Typography>
      </Stack>

      <Box
        sx={{
          minHeight: { xs: 420, md: 620 },
          borderRadius: 4,
          p: { xs: 1.25, md: 1.75 },
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          background: "radial-gradient(circle at top, #f8fafc 0%, #eef2f7 48%, #e8edf3 100%)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: { xs: 232, sm: 262, md: 286 },
            minHeight: { xs: 470, sm: 534, md: 596 },
            borderRadius: { xs: "32px", md: "38px" },
            overflow: "hidden",
            bgcolor: "#e5ddd5",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 26px 55px rgba(0,0,0,0.20)",
          }}
        >
          <Box sx={{ height: { xs: 38, md: 42 }, flexShrink: 0, bgcolor: "#075E54" }} />

          <Box
            sx={{
              px: 1.1,
              py: 1,
              bgcolor: "#075E54",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 0.8,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: { xs: 22, md: 26 },
                height: { xs: 22, md: 26 },
                borderRadius: "50%",
                bgcolor: "#d1fae5",
                color: "#075E54",
                display: "grid",
                placeItems: "center",
                fontWeight: 900,
                fontSize: { xs: 10, md: 12 },
              }}
            >
              B
            </Box>

            <Box>
              <Typography sx={{ fontSize: { xs: 11, md: 12.5 }, fontWeight: 900, lineHeight: 1.1 }}>
                Broadcast Preview
              </Typography>
              <Typography sx={{ fontSize: { xs: 8.5, md: 9.5 }, color: "rgba(255,255,255,0.84)", lineHeight: 1 }}>
                online
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              p: 1.2,
              backgroundImage: "radial-gradient(rgba(255,255,255,0.35) 1px, transparent 1px)",
              backgroundSize: "12px 12px",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", mb: 0.8 }}>
              <Box
                sx={{
                  px: 1,
                  py: 0.35,
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,0.78)",
                  color: "#64748B",
                  fontSize: 8,
                  fontWeight: 800,
                }}
              >
                TODAY
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
              <Box
                sx={{
                  maxWidth: "92%",
                  borderRadius: 3,
                  bgcolor: "white",
                  color: "#111827",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                  overflow: "hidden",
                }}
              >
                {renderHeader()}

                <Box sx={{ px: 1.5, py: 1.2 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: 10.4, md: 11.4 },
                      lineHeight: 1.48,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {fullPreviewText || "Your campaign message preview will appear here."}
                  </Typography>

                  {footerPreview ? (
                    <Typography sx={{ mt: 0.9, fontSize: 9, color: "#6B7280", lineHeight: 1.35 }}>
                      {footerPreview}
                    </Typography>
                  ) : null}

                  <Box sx={{ mt: 0.55, textAlign: "right", fontSize: 8, color: "#6B7280" }}>
                    {timeLabel}
                  </Box>
                </Box>

                {buttonsPreview.length > 0 ? (
                  <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                    {buttonsPreview.map((button, index) => (
                      <Box
                        key={`${button.type}-${button.text}-${index}`}
                        sx={{
                          px: 1.5,
                          py: 1.1,
                          borderTop: index === 0 ? "none" : "1px solid rgba(0,0,0,0.06)",
                          textAlign: "center",
                          color: "#128C7E",
                        }}
                      >
                        <Typography sx={{ fontSize: 10.4, fontWeight: 800, lineHeight: 1.25 }}>
                          {button.text}
                        </Typography>
                        {button.value ? (
                          <Typography sx={{ fontSize: 8.8, color: "#6B7280", mt: 0.25, lineHeight: 1.3 }}>
                            {button.value}
                          </Typography>
                        ) : null}
                      </Box>
                    ))}
                  </Box>
                ) : null}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              px: 1,
              py: 0.8,
              bgcolor: "#F0F2F5",
              borderTop: "1px solid rgba(0,0,0,0.06)",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                height: { xs: 28, md: 32 },
                px: 1.2,
                borderRadius: 999,
                bgcolor: "white",
                display: "flex",
                alignItems: "center",
                color: "#9CA3AF",
                fontSize: { xs: 9, md: 10 },
              }}
            >
              Message
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function CreateCampaignModal({ open, onClose, onSuccess, intelligenceAudienceId = null }) {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [form, setForm] = useState(defaultForm);
  const [audienceMode, setAudienceMode] = useState("simple");
  const [audienceChoice, setAudienceChoice] = useState("all_eligible");
  const [filters, setFilters] = useState(defaultFilters);
  const [excludeRecent, setExcludeRecent] = useState(true);
  const [recentDays, setRecentDays] = useState(7);
  const [previewRows, setPreviewRows] = useState([]);
  const [audienceSummary, setAudienceSummary] = useState(null);
  const [audiencePlan, setAudiencePlan] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templatesLoadedAt, setTemplatesLoadedAt] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [tplBody, setTplBody] = useState("");
  const [tplSchemaKeys, setTplSchemaKeys] = useState([]);
  const [templateVars, setTemplateVars] = useState({});
  const [uploadedHeaderMedia, setUploadedHeaderMedia] = useState(null);
  const [uploadingHeaderMedia, setUploadingHeaderMedia] = useState(false);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [whatsAppBudget, setWhatsAppBudget] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleLocal, setScheduleLocal] = useState(() => {
    const date = new Date();
    date.setMinutes(date.getMinutes() + 15);
    return toLocalDatetimeValue(date);
  });

  const canManageWhatsAppBudget = Boolean(
    currentUser?.is_admin
    || ["marketing", "admin", "superadmin"].includes(String(currentUser?.role || "").toLowerCase().trim())
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setActiveStep(0);
    setLoading(false);
    setErrorMsg("");
    setForm(defaultForm);
    setAudienceMode("simple");
    setAudienceChoice(intelligenceAudienceId ? "ai_intelligence" : "all_eligible");
    setFilters(defaultFilters);
    setExcludeRecent(true);
    setRecentDays(7);
    setPreviewRows([]);
    setAudienceSummary(null);
    setAudiencePlan(null);
    setSelectedIds(new Set());
    setTemplatesLoading(false);
    setTemplatesError("");
    setTemplates([]);
    setTemplatesLoadedAt(null);
    setSelectedTemplate(null);
    setTplBody("");
    setTplSchemaKeys([]);
    setTemplateVars({});
    setUploadedHeaderMedia(null);
    setUploadingHeaderMedia(false);
    setBudgetLoading(false);
    setWhatsAppBudget(null);

    const date = new Date();
    date.setMinutes(date.getMinutes() + 15);
    setScheduleLocal(toLocalDatetimeValue(date));
    setScheduleOpen(false);
  }, [open]);

  useEffect(() => {
    return () => {
      if (uploadedHeaderMedia?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(uploadedHeaderMedia.previewUrl);
      }
    };
  }, [uploadedHeaderMedia]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let active = true;

    (async () => {
      setBudgetLoading(true);
      try {
        const response = await api.get("/analytics/marketing/broadcast-budget");
        if (!active) {
          return;
        }
        setWhatsAppBudget(response?.data?.data?.channels?.whatsapp || null);
      } catch {
        if (active) {
          setWhatsAppBudget(null);
        }
      } finally {
        if (active) {
          setBudgetLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let active = true;

    (async () => {
      try {
        const response = await api.get("/users/me");
        if (active) {
          setCurrentUser(response?.data || null);
        }
      } catch {
        if (active) {
          setCurrentUser(null);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;

    (async () => {
      setTemplatesLoading(true);
      setTemplatesError("");

      try {
        const rows = await fetchWhatsAppTemplates();

        if (!cancelled) {
          setTemplates(rows);
          setTemplatesLoadedAt(new Date().toISOString());
        }
      } catch (error) {
        if (!cancelled) {
          setTemplates([]);
          setTemplatesError(error?.response?.data?.message || error?.message || "Failed to load WhatsApp templates.");
          setTemplatesLoadedAt(null);
        }
      } finally {
        if (!cancelled) {
          setTemplatesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const selectedTemplateId = safeStr(selectedTemplate?.id);

  useEffect(() => {
    const id = safeStr(form.whatsapp_template_id);
    const previousTemplateId = selectedTemplateId;

    if (!id) {
      setSelectedTemplate(null);
      setTplBody("");
      setTplSchemaKeys([]);
      setTemplateVars({});
      setUploadedHeaderMedia(null);
      setUploadingHeaderMedia(false);
      return;
    }

    const template = templates.find((item) => String(item?.id) === id);
    if (!template) {
      setSelectedTemplate(null);
      setTplBody("");
      setTplSchemaKeys([]);
      setTemplateVars({});
      setUploadedHeaderMedia(null);
      setUploadingHeaderMedia(false);
      return;
    }

    const preserveExistingValues = previousTemplateId && previousTemplateId === String(template?.id);

    setSelectedTemplate(template);
    setTplBody(String(template?.body || ""));

    const keys = schemaKeysSorted(template?.variables_schema);
    setTplSchemaKeys(keys);
    const templateFields = buildTemplateParameterFields(template);
    const defaults = getDefaultsForTemplate(template);

    if (!preserveExistingValues) {
      setUploadedHeaderMedia(null);
      setUploadingHeaderMedia(false);
    }

    setTemplateVars((current) => {
      const next = {};

      templateFields.forEach((field) => {
        const existing = preserveExistingValues ? safeStr(current?.[field.key]) : "";
        next[field.key] = existing || safeStr(defaults?.[field.key]) || "";
      });

      return next;
    });
  }, [form.whatsapp_template_id, selectedTemplateId, templates]);

  const templateParameterFields = useMemo(
    () => buildTemplateParameterFields(selectedTemplate),
    [selectedTemplate]
  );

  const requiredTemplateFields = useMemo(
    () => templateParameterFields.filter((field) => field.required !== false),
    [templateParameterFields]
  );

  const templateVarsValid = useMemo(() => {
    for (const field of requiredTemplateFields) {
      if (fieldValidationMessage(field, templateVars?.[field.key], selectedTemplate, templateVars)) {
        return false;
      }
    }

    return true;
  }, [requiredTemplateFields, selectedTemplate, templateVars]);

  const headerMediaFormat = useMemo(() => {
    const headerComponent = normalizedTemplateComponents(selectedTemplate).find((component) => templateComponentType(component) === "HEADER");
    const format = safeStr(headerComponent?.format).toUpperCase();

    return ["IMAGE", "VIDEO", "DOCUMENT"].includes(format) ? format : "";
  }, [selectedTemplate]);

  const clearUploadedHeaderMedia = () => {
    setTemplateVars((current) => ({
      ...current,
      header_media_id: "",
    }));

    setUploadedHeaderMedia((current) => {
      if (current?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return null;
    });
  };

  const handleHeaderMediaUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (event?.target) {
      event.target.value = "";
    }

    if (!file || !headerMediaFormat) {
      return;
    }

    const previewUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : "";

    setUploadingHeaderMedia(true);
    setErrorMsg("");

    try {
      const response = await campaignApi.uploadTemplateMedia(file, headerMediaFormat);
      const mediaId = safeStr(response?.data?.media_id);

      if (!mediaId) {
        throw new Error("Meta upload finished but no media ID was returned.");
      }

      setTemplateVars((current) => ({
        ...current,
        header_media_id: mediaId,
        header_media_link: "",
        header_document_filename:
          headerMediaFormat === "DOCUMENT"
            ? safeStr(current?.header_document_filename) || file.name
            : current?.header_document_filename || "",
      }));

      setUploadedHeaderMedia((current) => {
        if (current?.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(current.previewUrl);
        }

        return {
          mediaId,
          previewUrl,
          filename: file.name,
          mimeType: file.type,
          format: headerMediaFormat,
        };
      });
    } catch (error) {
      if (previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }

      setErrorMsg(error?.response?.data?.message || error?.message || "Failed to upload media to Meta.");
    } finally {
      setUploadingHeaderMedia(false);
    }
  };

  const selectedRecipientCount = selectedIds instanceof Set ? selectedIds.size : Array.isArray(selectedIds) ? selectedIds.length : 0;

  const canMoveForward = useMemo(() => {
    if (activeStep === 1) {
      if (!audiencePlan) {
        return false;
      }

      if (audiencePlan.manualSelection) {
        return selectedRecipientCount > 0;
      }

      return Number(audienceSummary?.eligibleFound || 0) > 0;
    }

    if (activeStep === 2) {
      const templateOk = safeStr(form.whatsapp_template_id).length > 0;
      const nameOk = safeStr(form.name).length >= 3;
      const perHour = Number(form.messages_per_hour);
      const cooldown = Number(form.cooldown_hours);
      const speedOk = Number.isFinite(perHour) && perHour >= 10;
      const cooldownOk = Number.isFinite(cooldown) && cooldown >= 0;

      return templateOk && nameOk && speedOk && cooldownOk && templateVarsValid;
    }

    return false;
  }, [activeStep, audiencePlan, audienceSummary, form, selectedRecipientCount, templateVarsValid]);

  const handleAudiencePrepared = (payload) => {
    if (!payload) {
      setPreviewRows([]);
      setAudienceSummary(null);
      setAudiencePlan(null);
      if (!(selectedIds instanceof Set) || selectedIds.size === 0) {
        setSelectedIds(new Set());
      }
      return;
    }

    setPreviewRows(payload.previewRows || []);
    setAudienceSummary(payload.summary || null);
    setAudiencePlan(payload.plan || null);

    if (!payload.plan?.manualSelection) {
      setSelectedIds(new Set());
    }
  };

  const handleUseAudience = () => {
    if (!audienceSummary || !audiencePlan) {
      return;
    }

    if (audiencePlan.manualSelection && Number(audienceSummary.totalFound || 0) <= 0) {
      return;
    }

    if (!audiencePlan.manualSelection && Number(audienceSummary.eligibleFound || 0) <= 0) {
      return;
    }

    setActiveStep(1);
  };

  const handleClose = () => {
    if (!loading) {
      onClose?.();
    }
  };

  const handleSubmit = async (scheduledAtValue = null) => {
    setLoading(true);
    setErrorMsg("");

    let campaignId = null;

    try {
      const templateId = safeStr(form.whatsapp_template_id);
      const varsEntries = templateParameterFields
        .map((field) => [String(field.key), safeStr(templateVars?.[field.key])])
        .filter(([, value]) => value !== "");
      const varsPayload = varsEntries.length > 0 ? Object.fromEntries(varsEntries) : null;

      const createPayload = {
        name: safeStr(form.name),
        whatsapp_template_id: templateId,
        template_variables: varsPayload,
        messages_per_hour: Number(form.messages_per_hour),
        cooldown_hours: Number(form.cooldown_hours),
        send_window_start: toApiTime(form.send_window_start),
        send_window_end: toApiTime(form.send_window_end),
        timezone: safeStr(form.timezone || "Africa/Harare"),
        schedule_at: scheduledAtValue ? safeStr(scheduledAtValue) : null,
        target_audience: filters.target_audience || "all",
      };

      const createResponse = await campaignApi.create(createPayload);
      campaignId = createResponse?.data?.campaign?.id;

      if (!campaignId) {
        throw new Error("Campaign created but no campaign id was returned.");
      }

      await campaignApi.resolveRecipients(campaignId, {
        mode: audiencePlan?.manualSelection ? "ids" : "filter",
        contact_ids: audiencePlan?.manualSelection
          ? Array.from(selectedIds).map(Number).filter(Number.isFinite)
          : [],
        source: safeStr(filters.source) || null,
        status: safeStr(filters.status) || null,
        q: safeStr(filters.q) || null,
        contact_group_id: safeStr(filters.contact_group_id) || null,
        segment: safeStr(audiencePlan?.filters?.segment || filters.segment) || null,
        exclude_recent: excludeRecent,
        days: Number(recentDays || 7),
        preferred_limit: audiencePlan?.filters?.preferred_limit || null,
        ...(audiencePlan?.filters?.audience_kind
          ? { audience_kind: audiencePlan.filters.audience_kind }
          : {}),
        ...(audiencePlan?.filters?.include_my_contacts
          ? { include_my_contacts: true }
          : {}),
      });

      onSuccess?.();
    } catch (error) {
      if (campaignId) {
        try {
          await campaignApi.remove(campaignId);
        } catch {
          // Ignore cleanup failures.
        }
      }

      setErrorMsg(error?.response?.data?.message || error?.message || "Failed to launch campaign.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSchedule = async () => {
    if (!scheduleLocal) {
      setScheduleOpen(false);
      await handleSubmit(null);
      return;
    }

    const date = new Date(scheduleLocal);
    if (Number.isNaN(date.getTime())) {
      setErrorMsg("Please choose a valid date and time.");
      return;
    }

    if (date.getTime() <= Date.now() + 30 * 1000) {
      setErrorMsg("Schedule time must be in the future.");
      return;
    }

    setScheduleOpen(false);
    await handleSubmit(scheduleLocal);
  };

  const handleRefreshTemplates = async () => {
    setTemplatesLoading(true);
    setTemplatesError("");

    try {
      const rows = await fetchWhatsAppTemplates();
      setTemplates(rows);
      setTemplatesLoadedAt(new Date().toISOString());
    } catch (error) {
      setTemplatesError(error?.response?.data?.message || error?.message || "Failed to load WhatsApp templates.");
    } finally {
      setTemplatesLoading(false);
    }
  };

  const renderTemplateUI = () => {
    const schema = selectedTemplate?.variables_schema || {};
    const keys = tplSchemaKeys || [];
    const templateDefaults = getDefaultsForTemplate(selectedTemplate);
    const hasAdvancedTemplateFields = templateParameterFields.some(
      (field) => field.key.startsWith("header_") || field.key.startsWith("button_")
    );

    const isDiscountPercentKey = (key) => {
      const label = safeStr(schema?.[String(key)]).toLowerCase();
      return label.includes("percentage") || label.includes("percent");
    };

    return (
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={900}>
              Message Template
            </Typography>
            {templatesLoadedAt ? (
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Last Meta sync: {formatTemplateSyncTime(templatesLoadedAt)}
              </Typography>
            ) : null}
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={templatesLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={handleRefreshTemplates}
            disabled={templatesLoading}
            sx={{ textTransform: "none", fontWeight: 800, alignSelf: { xs: "flex-start", sm: "center" } }}
          >
            Refresh Meta templates
          </Button>
        </Stack>

        {templatesLoading ? (
          <Alert icon={<CircularProgress size={16} />} severity="info">
            Loading templates...
          </Alert>
        ) : templatesError ? (
          <Alert severity="warning">{templatesError}</Alert>
        ) : templates.length === 0 ? (
          <Alert severity="warning">No templates returned from API.</Alert>
        ) : (
          <FormControl fullWidth>
            <Select
              id="wa-template"
              value={safeStr(form.whatsapp_template_id)}
              displayEmpty
              onChange={(event) => setForm((current) => ({
                ...current,
                whatsapp_template_id: String(event.target.value || ""),
              }))}
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1,
                    maxHeight: 420,
                  },
                },
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <Typography component="span" sx={{ color: "#9CA3AF" }}>
                      Select a template...
                    </Typography>
                  );
                }

                const template = templates.find((item) => String(item?.id) === String(selected));
                if (!template) {
                  return String(selected);
                }

                return (
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, color: "#111827", lineHeight: 1.2 }}>
                      {template.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#6B7280", lineHeight: 1.2, mt: 0.35 }}>
                      {describeTemplateForMenu(template) || (template.is_active ? "Approved template" : "Inactive template")}
                    </Typography>
                  </Box>
                );
              }}
              sx={{
                borderRadius: 2.5,
                bgcolor: "white",
                "& .MuiSelect-select": {
                  minHeight: "unset",
                  py: 1.4,
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              <MenuItem value="">
                <em>Select a template...</em>
              </MenuItem>
              {templates.map((template) => (
                <MenuItem
                  key={template.id}
                  value={String(template.id)}
                  disabled={!template.is_active}
                  sx={{ alignItems: "flex-start", py: 1.2 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                      {template.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#6B7280", lineHeight: 1.3, mt: 0.35 }}>
                      {describeTemplateForMenu(template) || (template.is_active ? "Approved template" : "Inactive template")}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" sx={{ mt: 0.75, color: "#6B7280" }}>
              Pick an approved Meta template, then fill the required variables below.
            </Typography>
          </FormControl>
        )}

        {tplBody && selectedTemplate ? (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              alignItems: "start",
              gridTemplateColumns: {
                xs: "1fr",
                xl: "minmax(0, 1.2fr) minmax(340px, 420px)",
              },
            }}
          >
            <Stack spacing={2}>
              <Box sx={{ border: "1px solid #E9ECEF", borderRadius: 2, p: 1.5, bgcolor: "#FAFAFA" }}>
                <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
                  Template Body
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {tplBody}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                  Schema variables: <strong>{keys.length ? keys.map((item) => `{{${item}}}`).join(", ") : "None"}</strong>. {"{{1}}"} is auto-filled from the recipient name.
                </Typography>
              </Box>

              {hasAdvancedTemplateFields ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This preview uses the synced Meta template schema, including header media and buttons. Meta may still apply small visual differences when the final message is sent.
                </Alert>
              ) : null}

              <Box sx={{ mt: 2, border: "1px solid #E9ECEF", borderRadius: 2, p: 1.5, bgcolor: "white" }}>
                <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                  Template Variables
                </Typography>

                {templateParameterFields.length > 0 ? (
                  <Stack spacing={2}>
                    {templateParameterFields.map((field) => {
                      const validationWithVars = fieldValidationMessage(field, templateVars?.[field.key], selectedTemplate, templateVars);
                      const hasExampleDefault = safeStr(templateDefaults?.[field.key]) !== "";
                      const uploadedMediaId = safeStr(templateVars?.header_media_id);
                      const helperText = validationWithVars
                        || (field.key === "header_media_link" && uploadedMediaId
                          ? `Uploaded to Meta successfully. Media ID: ${uploadedMediaId}`
                          : field.key === "header_media_link"
                          ? "Upload a file to Meta or enter your own public media URL. The preview may still use Meta sample media until you choose your own asset."
                          : (hasExampleDefault ? field.helperText : " "));

                      if (field.hidden) {
                        return null;
                      }

                      if (field.key === "header_media_link") {
                        return (
                          <Stack key={field.key} spacing={1.1}>
                            <TextField
                              fullWidth
                              label={field.label}
                              type={field.inputType || "text"}
                              value={String(templateVars?.[field.key] ?? "")}
                              onChange={(event) => setTemplateVars((current) => ({ ...current, [String(field.key)]: event.target.value }))}
                              error={Boolean(validationWithVars)}
                              helperText={helperText}
                              placeholder="https://cdn.example.com/whatsapp/header-image.jpg"
                            />

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                              <Button
                                component="label"
                                variant="outlined"
                                disabled={uploadingHeaderMedia || !headerMediaFormat}
                                sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}
                              >
                                {uploadingHeaderMedia
                                  ? "Uploading to Meta..."
                                  : headerMediaFormat
                                  ? `Upload ${headerMediaFormat.toLowerCase()} to Meta`
                                  : "Upload to Meta"}
                                <input
                                  hidden
                                  type="file"
                                  accept={headerMediaAcceptForFormat(headerMediaFormat)}
                                  onChange={handleHeaderMediaUpload}
                                />
                              </Button>

                              {safeStr(templateVars?.header_media_id) ? (
                                <Button
                                  variant="text"
                                  color="inherit"
                                  onClick={clearUploadedHeaderMedia}
                                  disabled={uploadingHeaderMedia}
                                  sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}
                                >
                                  Remove uploaded media
                                </Button>
                              ) : null}
                            </Stack>
                          </Stack>
                        );
                      }

                      return (
                        <TextField
                          key={field.key}
                          fullWidth
                          label={field.label}
                          type={field.inputType || "text"}
                          value={String(templateVars?.[field.key] ?? "")}
                          onChange={(event) => setTemplateVars((current) => ({ ...current, [String(field.key)]: event.target.value }))}
                          error={Boolean(validationWithVars)}
                          helperText={helperText}
                          inputProps={isDiscountPercentKey(field.key) ? { inputMode: "numeric" } : undefined}
                          InputProps={isDiscountPercentKey(field.key) ? { endAdornment: <InputAdornment position="end">%</InputAdornment> } : undefined}
                        />
                      );
                    })}
                  </Stack>
                ) : (
                  <Alert severity="info">No required variables beyond {"{{1}}"}.</Alert>
                )}
              </Box>
            </Stack>

            <Box sx={{ position: { xl: "sticky" }, top: { xl: 90 }, alignSelf: "start" }}>
                <WhatsAppPhonePreview body={tplBody} vars={templateVars} template={selectedTemplate} uploadedHeaderMedia={uploadedHeaderMedia} />
            </Box>
          </Box>
        ) : (
          <Alert severity="info">Select a template to preview and fill variables.</Alert>
        )}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "white",
          color: "#111827",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Toolbar sx={{ gap: 1.5, minHeight: 72 }}>
          <IconButton edge="start" onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>

          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              Create WhatsApp Broadcast
            </Typography>
            <Typography variant="caption" sx={{ color: "#6B7280" }}>
              Step {activeStep + 1} of {steps.length}
            </Typography>
          </Box>

          <Button onClick={handleClose} disabled={loading} sx={{ textTransform: "none", fontWeight: 800 }}>
            Cancel
          </Button>
        </Toolbar>

        <Box sx={{ px: { xs: 2, md: 3 }, pb: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel sx={{ "& .MuiStepLabel-label": { fontWeight: 800 } }}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </AppBar>

      <Box sx={{ bgcolor: "#F3F4F6", minHeight: "100vh", pb: 12 }}>
        <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, width: "100%" }}>
          {errorMsg ? (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error" onClose={() => setErrorMsg("")}>
                {errorMsg}
              </Alert>
            </Box>
          ) : null}

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              border: "1px solid #E5E7EB",
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "white",
            }}
          >
            <Box sx={{ p: { xs: 2, md: 3 }, width: "100%", display: "flex" }}>
              <Box sx={{ width: "100%", maxWidth: "100%" }}>
                {activeStep === 0 ? (
                  <AudienceBuilder
                    audienceMode={audienceMode}
                    onAudienceModeChange={setAudienceMode}
                    audienceChoice={audienceChoice}
                    onAudienceChoiceChange={setAudienceChoice}
                    filters={filters}
                    onFiltersChange={setFilters}
                    loading={loading}
                    setLoading={setLoading}
                    excludeRecent={excludeRecent}
                    setExcludeRecent={setExcludeRecent}
                    recentDays={recentDays}
                    setRecentDays={setRecentDays}
                    audienceSummary={audienceSummary}
                    previewRows={previewRows}
                    onAudiencePrepared={handleAudiencePrepared}
                    onUseAudience={handleUseAudience}
                    whatsAppBudget={whatsAppBudget}
                    budgetLoading={budgetLoading}
                    canManageBudget={canManageWhatsAppBudget}
                    onWhatsAppBudgetUpdated={setWhatsAppBudget}
                    intelligenceAudienceId={intelligenceAudienceId}
                  />
                ) : null}

                {activeStep === 1 ? (
                  <AudienceReview
                    audienceChoice={audienceChoice}
                    filters={filters}
                    audiencePlan={audiencePlan}
                    audienceSummary={audienceSummary}
                    previewRows={previewRows}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    onFiltersChange={setFilters}
                  />
                ) : null}

                {activeStep === 2 ? (
                  <Stack spacing={2.5}>
                    <AudienceSummaryCard
                      audiencePlan={audiencePlan}
                      audienceSummary={audienceSummary}
                      selectedRecipientCount={selectedRecipientCount}
                    />

                    <TextField
                      fullWidth
                      label="Campaign Name"
                      value={form.name}
                      onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                      placeholder="e.g. Hot Leads Follow-up"
                    />

                    {renderTemplateUI()}

                    <Divider />

                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                      <TextField
                        fullWidth
                        label="Messages per hour"
                        type="number"
                        value={form.messages_per_hour}
                        onChange={(event) => setForm((current) => ({ ...current, messages_per_hour: event.target.value }))}
                        inputProps={{ min: 10, max: 5000 }}
                      />
                      <TextField
                        fullWidth
                        label="Cooldown hours"
                        type="number"
                        value={form.cooldown_hours}
                        onChange={(event) => setForm((current) => ({ ...current, cooldown_hours: event.target.value }))}
                        inputProps={{ min: 0, max: 720 }}
                      />
                    </Stack>

                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                      <TextField
                        fullWidth
                        label="Send window starts"
                        type="time"
                        value={form.send_window_start}
                        onChange={(event) => setForm((current) => ({ ...current, send_window_start: event.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        helperText="Messages are released at the hourly rate only inside this window."
                      />
                      <TextField
                        fullWidth
                        label="Send window ends"
                        type="time"
                        value={form.send_window_end}
                        onChange={(event) => setForm((current) => ({ ...current, send_window_end: event.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        helperText="Anything left over continues in the next day's window."
                      />
                    </Stack>

                    <TextField
                      fullWidth
                      label="Timezone"
                      value={form.timezone}
                      onChange={(event) => setForm((current) => ({ ...current, timezone: event.target.value }))}
                    />
                  </Stack>
                ) : null}

                {activeStep === 3 ? (
                  <CampaignConfirmation
                    audienceChoice={audienceChoice}
                    filters={filters}
                    audienceSummary={audienceSummary}
                    selectedIds={selectedIds}
                    audiencePlan={audiencePlan}
                    templateName={selectedTemplate?.name || safeStr(form.whatsapp_template_id)}
                    messagesPerHour={form.messages_per_hour}
                    cooldownHours={form.cooldown_hours}
                    sendWindowStart={form.send_window_start}
                    sendWindowEnd={form.send_window_end}
                    targetAudience={filters.target_audience}
                    timezone={form.timezone}
                  />
                ) : null}
              </Box>
            </Box>

            <Divider />

            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 1.5,
                bgcolor: "#FAFAFA",
                borderTop: "1px solid #E5E7EB",
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                {activeStep === 0
                  ? "Tip: Choose the audience outcome first, then use Find Audience to confirm the sendable count."
                  : "Tip: This flow is designed so the sender understands the audience outcome before the broadcast is launched."}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>

      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(8px)",
          borderTop: "1px solid #E5E7EB",
          p: 2,
          zIndex: 20,
        }}
      >
        <Box sx={{ px: { xs: 0, md: 1 }, display: "flex", alignItems: "center", gap: 1 }}>
          {activeStep > 0 ? (
            <Button
              onClick={() => setActiveStep((previous) => Math.max(0, previous - 1))}
              disabled={loading}
              sx={{ textTransform: "none", fontWeight: 900 }}
            >
              Back
            </Button>
          ) : null}

          <Box sx={{ flex: 1 }} />

          {activeStep === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Use <strong>Use This Audience</strong> above to continue.
            </Typography>
          ) : null}

          {activeStep > 0 && activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => canMoveForward && setActiveStep((previous) => previous + 1)}
              disabled={!canMoveForward || loading}
              sx={{ textTransform: "none", fontWeight: 900, px: 3 }}
            >
              Next
            </Button>
          ) : null}

          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<CalendarMonthIcon />}
              onClick={() => setScheduleOpen(true)}
              disabled={loading}
              sx={{ textTransform: "none", fontWeight: 900, px: 3 }}
            >
              {loading ? "Scheduling..." : "Schedule Broadcast"}
            </Button>
          ) : null}
        </Box>
      </Box>

      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Schedule broadcast</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#6B7280", mb: 2 }}>
            Pick when the broadcast should run. Leave the field blank if you want it to start immediately.
          </Typography>

          <TextField
            label="Run date & time"
            type="datetime-local"
            fullWidth
            value={scheduleLocal}
            onChange={(event) => setScheduleLocal(event.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Must be in the future when provided."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setScheduleOpen(false)} sx={{ textTransform: "none", fontWeight: 800 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmSchedule}
            disabled={loading}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            Confirm schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

function AudienceSummaryCard({ audiencePlan, audienceSummary, selectedRecipientCount }) {
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #E5E7EB", bgcolor: "#FAFAFA" }}>
      <Stack spacing={0.5}>
        <Typography variant="subtitle2" fontWeight={900}>
          Audience locked in
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {audiencePlan?.manualSelection
            ? `You have picked ${selectedRecipientCount.toLocaleString()} recipients for this broadcast. Guardrails will still be re-checked before queueing.`
            : `This broadcast will use the reviewed audience. Estimated sendable count: ${Number(audienceSummary?.eligibleFound || 0).toLocaleString()}.`}
        </Typography>
      </Stack>
    </Paper>
  );
}
